import React, { useMemo, useRef } from 'react';
import { pointsDistance } from '@/utils/math-kit';
import { IImageLayerProps, ILayerProps, LayerType } from '@/version-types';
import './image-operate.less';
import { IFramework, useIsBasicOperate, useScale } from '@/framework';
import { useMoveImageOperateRealTime } from './hooks/use-move-operate';
import { useRotateImageOperateRealTime } from './hooks/use-rotate-operate';
import { useScaleImageOperateRealTime } from './hooks/use-scale-operate';

interface IImageOperateProps {
    framework: IFramework;
    activeData: {
        layer: ILayerProps | null;
        pickLayer: ILayerProps | null;
    } | null;
}

const DefaultRotateDistance = 50;
const BaseScaleWidth = 50;

export const ImageOperate: React.FC<IImageOperateProps> = React.memo(({ framework, activeData }) => {
    const layerCenterNode = useRef<HTMLDivElement>(null);
    const scale = useScale();
    const activeLayer = (activeData?.pickLayer || activeData?.layer) as ILayerProps;

    const isBasicOperate = useIsBasicOperate();
    const { rotateMovePoint, layer } = useRotateImageOperateRealTime(activeLayer, framework);
    const { moveX } = useScaleImageOperateRealTime(framework);
    useMoveImageOperateRealTime(scale, framework);

    const imgRotateInnerStyle: React.CSSProperties = useMemo(() => {
        const rect = layerCenterNode.current?.getBoundingClientRect();
        let rotateDistance = DefaultRotateDistance;
        if (rect && rotateMovePoint) {
            rotateDistance = pointsDistance(
                {
                    x: rect.x,
                    y: rect.y,
                },
                rotateMovePoint,
            );
        } else {
            rotateDistance = DefaultRotateDistance;
        }
        return {
            width: rotateMovePoint ? `${rotateDistance}px` : '50px',
            left: rotateMovePoint ? 0 : '23px',
        };
    }, [rotateMovePoint]);

    if (isBasicOperate) return null;

    if (layer?.layerType !== LayerType.IMAGE_LAYER) return null;
    if (!(layer.data as IImageLayerProps).maskURL) return null;
    if (layer?.__renderGLCanvas) return null;

    const data = layer.data as IImageLayerProps;
    const { rotate, flipH, flipV } = data.imageTransform;

    let angle = rotate;

    if (flipH) {
        angle = -rotate;
    }

    if (flipV) {
        angle = -rotate;
    }

    if (flipH && flipV) {
        angle = rotate;
    }

    let scaleWidth = BaseScaleWidth + moveX * 2;
    scaleWidth = scaleWidth < 0 ? 0 : scaleWidth > BaseScaleWidth * 3 ? BaseScaleWidth * 3 : scaleWidth;

    let scaleLeft = moveX;
    scaleLeft =
        scaleLeft < -BaseScaleWidth / 2 ? -BaseScaleWidth / 2 : scaleLeft > BaseScaleWidth ? BaseScaleWidth : scaleLeft;

    return (
        <div
            className="l-layer-img-operate"
            style={{
                width: moveX ? `${scaleWidth}px` : `${BaseScaleWidth}px`,
                height: moveX ? `${scaleWidth}px` : `${BaseScaleWidth}px`,
            }}
        >
            <div className="bg"></div>

            <div data-operate-type="imgMove" className="img-move-wrap" style={{ pointerEvents: 'auto' }}>
                <div ref={layerCenterNode} className="point-event-none img-move"></div>

                <div className="point-event-none img-move"></div>
            </div>

            <div className="img-rotate-wrap" style={{ transform: `rotate(${angle}deg)` }}>
                <div className="img-rotate-inner" style={imgRotateInnerStyle}>
                    <div className="img-rotate" data-operate-type="imgRotate" style={{ pointerEvents: 'auto' }}></div>
                </div>
            </div>

            <div className="img-scale-wrap">
                <div className="img-scale-inner">
                    <div
                        style={{
                            right: `${-scaleLeft}px`,
                            pointerEvents: 'auto',
                        }}
                        className="img-scale"
                        data-operate-type="imgScale"
                    ></div>
                </div>
            </div>
        </div>
    );
});

ImageOperate.displayName = 'ImageOperate';

export default ImageOperate;
